import { Button, StatusChip } from '@scripsteam/scrips-signal-ds';
import {
  awaitingLabel,
  labGroupMetaLine,
  type LabResultGroup,
  type LabTestGroup,
} from '@/patients/lib/lab-results';
import { labProvenanceLine } from '@/patients/lib/lab-provenance';
import { useLabUserNames } from '@/patients/services/use-lab-user-names';
import { LabAnalyteRowView } from './lab-analyte-row';
import { V2_BODY_EMPHASIS, V2_CARD_EDGE, v2Look } from '@/shared/ui/design-v2';

export interface ViewReportTarget {
  patientDocumentId: string;
  /** An already-signed URL for it, when the lab desk list carried one. */
  url?: string | null;
}

interface LabRequestCardProps {
  patientId: string;
  group: LabResultGroup;
  onViewReport: (target: ViewReportTarget) => void;
  /**
   * The patient's documents are still loading. A report PDF with no signed URL of its own waits
   * for them (disabled), so its click can open synchronously and is never popup-blocked.
   */
  documentsLoading?: boolean;
}

const COLS = 6;

/** One lab request: header with completeness, then its tests and analytes (PROD-2643). */
export function LabRequestCard({
  patientId,
  group,
  onViewReport,
  documentsLoading = false,
}: LabRequestCardProps) {
  const preliminary = group.reports.some((r) => r.preliminary);
  const amended = group.reports.some((r) => r.amended);
  // Who confirmed each extracted report: names for the ids the API sends (never a raw id).
  const confirmers = useLabUserNames(group.reports.map((r) => r.provenance.confirmedBy));
  // Keyed by report id: two reports can share a confirmer and a time, so the text is not unique.
  const provenanceLines = group.reports
    .map((r) => ({ id: r.id, line: labProvenanceLine(r.provenance, confirmers.get(r.provenance.confirmedBy ?? '')) }))
    .filter((p): p is { id: string; line: string } => !!p.line);
  const reportDocs = uniqueDocs(group);
  const hasRows = group.tests.length > 0 || group.otherAnalytes.length > 0;
  const conclusions = group.reports.map((r) => r.conclusion).filter(Boolean) as string[];

  return (
    <section
      className={v2Look('rounded-lg border border-border-default overflow-hidden bg-surface-card', V2_CARD_EDGE)}
      data-testid="lab-request-card"
      data-slot="lab-card"
      aria-label={group.name}
    >
      {/* The design PaLabRequests card chrome (showcase-patient.css, section "labs card"), unconditional. */}
      <header data-slot="lab-card-head" className="flex flex-wrap items-start justify-between gap-3 px-3 py-2 bg-grey-100">
        <div data-slot="lab-card-head-text" className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span data-slot="lab-card-name" className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>{group.name}</span>
            {preliminary ? (
              <StatusChip tone="soft" semantic="warning" size="xs" data-testid="lab-marker-preliminary">
                Preliminary
              </StatusChip>
            ) : null}
            {amended ? (
              <StatusChip tone="soft" semantic="info" size="xs" data-testid="lab-marker-amended">
                Amended
              </StatusChip>
            ) : null}
          </div>
          <div className="text-xs text-text-muted" data-slot="lab-card-dates" data-testid="lab-request-dates">
            {labGroupMetaLine(group)}
          </div>
          {provenanceLines.map(({ id, line }) => (
            <div key={id} className="text-xs text-text-hint" data-slot="lab-card-dates" data-testid="lab-marker-extracted">
              {line}
            </div>
          ))}
        </div>
        <div data-slot="lab-card-head-right" className="flex items-center gap-3">
          {group.totalCount > 0 ? (
            <span className="text-sm text-text-secondary" data-slot="lab-card-complete" data-testid="lab-completeness">
              {group.resultedCount} / {group.totalCount} resulted
              {group.notPerformedCount > 0 ? (
                <span className="text-text-hint" data-slot="lab-card-hint" data-testid="lab-not-performed">
                  {' '}
                  · {group.notPerformedCount} not performed
                </span>
              ) : null}
            </span>
          ) : null}
          {reportDocs.map((doc, i) => (
            <Button
              key={doc.patientDocumentId}
              data-slot="lab-card-report"
              variant="tertiary"
              size="sm"
              disabled={!doc.url && documentsLoading}
              onClick={() => onViewReport(doc)}
            >
              {reportDocs.length > 1 ? `View report ${i + 1}` : 'View report'}
            </Button>
          ))}
        </div>
      </header>

      {hasRows ? (
        <table data-slot="lab-card-table" className="w-full text-sm">
          <thead>
            <tr className="text-left text-text-muted border-t border-border-default">
              <Th>Test</Th>
              <Th>Result</Th>
              <Th>Unit</Th>
              <Th>Range</Th>
              <Th>Flag</Th>
              <Th>Trend</Th>
            </tr>
          </thead>
          <tbody>
            {group.tests.map((test) => (
              <TestRows key={test.id} patientId={patientId} test={test} />
            ))}
            {group.otherAnalytes.length > 0 ? (
              <>
                {group.tests.length > 0 ? <LabelRow>Other results</LabelRow> : null}
                {group.otherAnalytes.map((a) => (
                  <LabAnalyteRowView
                    key={a.id}
                    patientId={patientId}
                    analyte={a}
                    nested={group.tests.length > 0}
                  />
                ))}
              </>
            ) : null}
          </tbody>
        </table>
      ) : (
        <p data-slot="lab-card-empty" className="px-3 py-3 text-sm text-text-hint border-t border-border-default">
          No discrete results yet.
        </p>
      )}

      {conclusions.length > 0 ? (
        <p className="px-3 py-2 text-sm text-text-secondary border-t border-border-default">
          {conclusions.join(' ')}
        </p>
      ) : null}
    </section>
  );
}

function TestRows({ patientId, test }: { patientId: string; test: LabTestGroup }) {
  if (test.analytes.length === 1) {
    return <LabAnalyteRowView patientId={patientId} analyte={test.analytes[0]} />;
  }
  if (test.analytes.length > 1) {
    return (
      <>
        <LabelRow>{test.display}</LabelRow>
        {test.analytes.map((a) => (
          <LabAnalyteRowView key={a.id} patientId={patientId} analyte={a} nested />
        ))}
      </>
    );
  }
  return (
    <tr className="border-t border-border-default" data-testid="lab-test-pending">
      <td className="px-3 py-2 align-top text-text-primary">{test.display}</td>
      <td className="px-3 py-2 align-top" colSpan={COLS - 1}>
        {test.state === 'cancelled' ? (
          <StatusChip
            tone="soft"
            semantic="neutral"
            size="xs"
            className="line-through"
            data-testid="lab-status-cancelled"
          >
            Cancelled
          </StatusChip>
        ) : (
          <span data-slot="lab-card-pending" className="text-text-hint italic">{awaitingLabel(test.state) ?? '—'}</span>
        )}
      </td>
    </tr>
  );
}

function LabelRow({ children }: { children: React.ReactNode }) {
  return (
    <tr className="border-t border-border-default">
      <td className="px-3 py-2 font-medium text-text-primary" colSpan={COLS}>
        {children}
      </td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 text-xs font-semibold text-left">{children}</th>
  );
}

/** Each distinct PDF once: the current reports' PDFs, else the one linked on the request. */
function uniqueDocs(group: LabResultGroup): ViewReportTarget[] {
  const out: ViewReportTarget[] = [];
  const seen = new Set<string>();
  const add = (id: string | null) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push({
      patientDocumentId: id,
      url: id === group.requestDocumentId ? group.requestDocumentUrl : null,
    });
  };
  group.reports.forEach((r) => add(r.patientDocumentId));
  if (out.length === 0) add(group.requestDocumentId);
  return out;
}
